// Central artist data for Yacht Rock Detroit 2026.
// Used by /lineup grid and /lineup/:slug detail pages.

export type ArtistDay = "friday" | "saturday";

export interface ArtistHit {
  title: string;
  context?: string;
}

export interface ArtistData {
  slug: string;
  name: string;
  role?: string;
  shortTagline: string;
  bigStatement: string;
  image: string;
  imageMobile?: string;
  logoUrl?: string;
  accentColor?: string;
  imageAlt: string;
  bio: string;
  longBio: string;
  topSongs: string[];
  hits: ArtistHit[];
  day: ArtistDay;
  dayLabel: string;
  dayDate: string;
  position: number;
  revealed: boolean;
  spotifyUrl?: string;
  wikipediaUrl?: string;
  externalUrl?: string;
  instagramUrl?: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  youtubeId?: string;
}

const FRIDAY_DATE = "Friday, July 17, 2026";
const SATURDAY_DATE = "Saturday, July 18, 2026";

export const ARTISTS: ArtistData[] = [
  // ──────── FRIDAY ────────
  {
    slug: "yacht-rock-revue",
    name: "Yacht Rock Revue",
    role: "The World's Premier Soft-Rock Party Band",
    shortTagline: "The band that started the yacht rock revival.",
    bigStatement:
      "They built a movement. Now they're docking in Detroit.",
    image: "/artists/yacht-rock-revue.jpg",
    imageAlt: "Yacht Rock Revue full band performing on stage with packed crowd",
    bio: "Hailed by Rolling Stone as the 'world's premier soft-rock party band,' Yacht Rock Revue delivers dy-no-mite renditions of hits by Kenny Loggins, Michael McDonald, Steely Dan, and Christopher Cross.",
    longBio:
      "Yacht Rock Revue didn't just ride the yacht rock wave — they built it. Out of Atlanta in 2007, they turned a niche '70s/'80s sound into a national movement. Rolling Stone called them the world's premier soft-rock party band. Their original record 'Hot Dads in Tight Jeans' went viral. They've shared stages with Hall & Oates, Kenny Loggins, John Oates, and Eddie Money. 2026 marks their most ambitious live production yet — and they're bringing it to the Detroit River for the first time.",
    topSongs: ["Escape Artist", "Step", "Tropical Illusion"],
    hits: [
      { title: "Hold On (Cover)", context: "Their viral take on the Michael McDonald classic" },
      { title: "Escape Artist", context: "Original — title track of their 2018 album" },
      { title: "Africa (Cover)", context: "The Toto anthem that broke them on YouTube" },
      { title: "Step", context: "Original — modern yacht rock at its most propulsive" },
    ],
    day: "friday",
    dayLabel: "Friday",
    dayDate: FRIDAY_DATE,
    position: 1,
    revealed: true,
    spotifyUrl: "https://open.spotify.com/artist/0V1hEBOwFHEFMl00FwCYzp",
    externalUrl: "https://www.yachtrockrevue.com",
    metaTitle:
      "Yacht Rock Revue Live in Detroit — Friday July 17, 2026 | Yacht Rock Detroit",
    metaDescription:
      "See Yacht Rock Revue live in Detroit on Friday, July 17, 2026 at The Roostertail. Rolling Stone's 'world's premier soft-rock party band' headlines Yacht Rock Detroit night one on the Detroit River.",
    metaKeywords:
      "yacht rock revue detroit, yacht rock revue tour 2026, yacht rock revue tickets, yacht rock revue live, detroit concert july 17, yacht rock detroit lineup",
  },
  {
    slug: "pablo-cruise",
    name: "Pablo Cruise",
    role: "Yacht Rock Legends",
    shortTagline: "California sound. Detroit waterfront.",
    bigStatement:
      "Detroit's on the up. And Pablo Cruise is bringing California to the river.",
    image: "/artists/pablo-cruise.jpg",
    imageAlt: "Pablo Cruise band on the pier",
    bio: "Pablo Cruise defined the California yacht rock sound with hits like 'Love Will Find a Way' and 'Whatcha Gonna Do?' Now they're performing live in Detroit at The Roostertail, bringing warm Pacific grooves to the Detroit River.",
    longBio:
      "Pablo Cruise are the originators. Out of San Francisco in 1973, they bottled California sunshine into songs that defined the soft-rock era — 'Love Will Find a Way,' 'Whatcha Gonna Do?,' 'I Want You Tonight,' 'A Place in the Sun.' Three of their albums went gold. They toured arenas with Heart and Fleetwood Mac. Keyboardist Cory Lerios and bassist Bud Cockrell are still leading the band, still delivering those warm Pacific harmonies live. This summer, they're crossing the country to do it on the Detroit River — the kind of crowd, the kind of sunset, the songs were written for.",
    topSongs: [
      "Love Will Find a Way",
      "Whatcha Gonna Do?",
      "A Place in the Sun",
    ],
    hits: [
      { title: "Love Will Find a Way", context: "1978 — peaked #6 on Billboard Hot 100" },
      { title: "Whatcha Gonna Do?", context: "1977 — the song that broke them nationally" },
      { title: "A Place in the Sun", context: "1977 — gold-certified album, defining track" },
      { title: "I Want You Tonight", context: "1979 — peaked #19 on Billboard Hot 100" },
    ],
    day: "friday",
    dayLabel: "Friday",
    dayDate: FRIDAY_DATE,
    position: 2,
    revealed: true,
    spotifyUrl: "https://open.spotify.com/artist/3xMfMDJaAWdCDMlXsYPVox",
    wikipediaUrl: "https://en.wikipedia.org/wiki/Pablo_Cruise",
    metaTitle:
      "Pablo Cruise Live in Detroit — Friday July 17, 2026 | Yacht Rock Detroit",
    metaDescription:
      "Pablo Cruise performs live in Detroit on Friday, July 17, 2026 at The Roostertail on the Detroit River. Hear 'Love Will Find a Way,' 'Whatcha Gonna Do?,' and the yacht rock hits that defined a generation.",
    metaKeywords:
      "pablo cruise detroit, pablo cruise tickets 2026, pablo cruise live, pablo cruise tour, love will find a way live, whatcha gonna do live, detroit concert pablo cruise",
  },
  {
    slug: "seven-wonders",
    name: "Seven Wonders",
    role: "Fleetwood Mac Tribute",
    shortTagline: "The most faithful Fleetwood Mac on the road.",
    bigStatement:
      "The Chain. Go Your Own Way. Dreams. All of it. On the river.",
    image: "/artists/seven-wonders.jpg",
    imageAlt: "Seven Wonders performing at sunset on the Detroit waterfront",
    bio: "Seven Wonders is the premier Fleetwood Mac tribute band, faithfully recreating the magic of 'Go Your Own Way,' 'Dreams,' and 'The Chain.' They open Yacht Rock Detroit 2026 on Friday at The Roostertail in Detroit.",
    longBio:
      "Seven Wonders earned their reputation the hard way — by being uncompromisingly faithful. Stevie's phrasing. Lindsey's guitar work. Christine's keys. The vocal blends that made Rumours the album you can still hear on the radio every single day. They open Yacht Rock Detroit 2026 on Friday night — first band on the river — and they're going to set the tone for the entire weekend.",
    topSongs: ["Go Your Own Way", "Dreams", "The Chain"],
    hits: [
      { title: "Go Your Own Way", context: "The Buckingham anthem from Rumours (1977)" },
      { title: "Dreams", context: "Nicks' #1 hit, the band's only US chart-topper" },
      { title: "The Chain", context: "The only song co-written by all five members" },
      { title: "Don't Stop", context: "1977's enduring optimism, still in heavy rotation" },
    ],
    day: "friday",
    dayLabel: "Friday",
    dayDate: FRIDAY_DATE,
    position: 3,
    revealed: true,
    metaTitle:
      "Seven Wonders Fleetwood Mac Tribute Live in Detroit — Friday July 17, 2026",
    metaDescription:
      "Seven Wonders, the premier Fleetwood Mac tribute band, opens Yacht Rock Detroit on Friday, July 17, 2026 at The Roostertail. Hear 'Dreams,' 'Go Your Own Way,' and 'The Chain' live on the Detroit waterfront.",
    metaKeywords:
      "seven wonders fleetwood mac tribute, fleetwood mac tribute detroit, seven wonders detroit, fleetwood mac live detroit, dreams live, go your own way tribute",
  },

  // ──────── SATURDAY ────────
  {
    slug: "three-dog-night",
    name: "Three Dog Night",
    role: 'Saturday Headliner',
    shortTagline: "Three voices. Twenty-one Top 40 hits. One night in Detroit.",
    bigStatement:
      "21 Top 40 hits. 7 million-sellers. One Saturday on the Detroit River.",
    image: "/artists/three-dog-night.jpg",
    imageAlt: "Three Dog Night performing live with dramatic blue stage lights",
    bio: "Three Dog Night delivered era-defining hits like 'One,' 'Joy to the World,' and 'Mama Told Me Not to Come.' They headline Saturday night at Yacht Rock Detroit 2026, performing live at The Roostertail on the Detroit waterfront.",
    longBio:
      "Three Dog Night didn't just have hits — they had the soundtrack. Between 1969 and 1975, they put 21 songs in the Top 40, including 11 Top 10s and 3 #1s. 'Joy to the World.' 'One.' 'Mama Told Me Not to Come.' 'Black & White.' These weren't just radio singles — they were the songs of a generation. Original member Danny Hutton still leads the band, still hits the harmonies that made them legendary. They've sold over 90 million records. They're touring 50+ cities a year. And on Saturday, July 18, 2026, they're headlining Yacht Rock Detroit on the river.",
    topSongs: ["One", "Joy to the World", "Mama Told Me Not to Come"],
    hits: [
      { title: "Joy to the World", context: "#1 in 1971 — six weeks at the top" },
      { title: "One", context: "Their breakthrough — #5 in 1969" },
      { title: "Mama Told Me Not to Come", context: "#1 in 1970, written by Randy Newman" },
      { title: "Black & White", context: "#1 in 1972 — civil rights anthem turned hit" },
      { title: "Old Fashioned Love Song", context: "#4 in 1971, written by Paul Williams" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 1,
    revealed: true,
    spotifyUrl: "https://open.spotify.com/artist/4FAEZeJcsYYBkNq2D3KBYA",
    wikipediaUrl: "https://en.wikipedia.org/wiki/Three_Dog_Night",
    externalUrl: "https://www.threedognight.com",
    metaTitle:
      "Three Dog Night Live in Detroit — Saturday July 18, 2026 | Yacht Rock Detroit",
    metaDescription:
      "Three Dog Night headlines Yacht Rock Detroit on Saturday, July 18, 2026 at The Roostertail on the Detroit River. 21 Top 40 hits, including 'Joy to the World,' 'One,' and 'Mama Told Me Not to Come' — live.",
    metaKeywords:
      "three dog night detroit, three dog night tickets 2026, three dog night tour, three dog night live, joy to the world live, three dog night roostertail, three dog night detroit concert",
  },
  {
    slug: "ambrosia",
    name: "Ambrosia",
    role: '"Biggest Part of Me"',
    shortTagline: "Three-time Grammy nominees. Smoothest band in the lineup.",
    bigStatement:
      "Smoother than anything else on stage. Saturday on the river.",
    image: "/artists/ambrosia.jpg",
    imageAlt: "Concert crowd silhouette under orange and blue stage lights",
    bio: "Ambrosia delivered some of the smoothest yacht rock hits of the late '70s and early '80s, including 'Biggest Part of Me' and 'How Much I Feel.' They perform live at Yacht Rock Detroit 2026 on the Detroit waterfront at The Roostertail.",
    longBio:
      "Ambrosia were art-rock kids who turned into yacht rock royalty. Three Grammy nominations. Five gold and platinum albums. Four Top 40 singles. They worked with Alan Parsons. They had Bruce Hornsby in the touring band. And David Pack's voice on 'Biggest Part of Me' is one of the smoothest sounds of the late '70s — period. Pack and original members Joe Puerta and Burleigh Drummond still tour and still hit those harmonies. They take the Saturday stage at The Roostertail on July 18.",
    topSongs: [
      "Biggest Part of Me",
      "How Much I Feel",
      "You're the Only Woman",
    ],
    hits: [
      { title: "Biggest Part of Me", context: "#3 in 1980 — Grammy-nominated" },
      { title: "How Much I Feel", context: "#3 in 1978 — Grammy-nominated" },
      { title: "You're the Only Woman", context: "#13 in 1980" },
      { title: "Holdin' On to Yesterday", context: "#17 in 1975 — their first hit" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 2,
    revealed: true,
    spotifyUrl: "https://open.spotify.com/artist/3MATYZ0YJQc0iB8MqzOzFk",
    wikipediaUrl: "https://en.wikipedia.org/wiki/Ambrosia_(band)",
    metaTitle:
      "Ambrosia Live in Detroit — Saturday July 18, 2026 | Yacht Rock Detroit",
    metaDescription:
      "Ambrosia performs live in Detroit on Saturday, July 18, 2026 at The Roostertail. Hear 'Biggest Part of Me,' 'How Much I Feel,' and the smoothest yacht rock hits of the late '70s.",
    metaKeywords:
      "ambrosia band detroit, ambrosia tickets 2026, ambrosia live, biggest part of me live, ambrosia tour, ambrosia roostertail",
  },
  {
    slug: "elliot-lurie",
    name: "Elliot Lurie",
    role: 'The Voice of Looking Glass — "Brandy"',
    shortTagline: 'The voice behind "Brandy (You\'re a Fine Girl)."',
    bigStatement:
      'One of the most recognizable voices of the 1970s. Live in Detroit.',
    image: "/artists/elliot-lurie.jpg",
    imageAlt: "Elliot Lurie singing into a vintage chrome microphone, warm cabaret stage light",
    bio: "Elliot Lurie, frontman of Looking Glass, wrote and sang 'Brandy (You're a Fine Girl)' — one of the defining yacht rock anthems of the 1970s. He brings that timeless hit to Detroit's waterfront at The Roostertail for Yacht Rock Detroit 2026.",
    longBio:
      "Elliot Lurie was 23 when he wrote 'Brandy (You're a Fine Girl).' The song hit #1 on the Billboard Hot 100 in August 1972, sold over a million copies, and has been on rotation ever since — from the soundtrack of Guardians of the Galaxy Vol. 2 to every yacht rock playlist ever made. Lurie wrote it, sang it, played guitar on it. He's still doing it live — and he's bringing it to the Detroit waterfront on Saturday, July 18, 2026.",
    topSongs: [
      "Brandy (You're a Fine Girl)",
      "Jimmy Loves Mary-Anne",
      "Golden Rainbow",
    ],
    hits: [
      { title: "Brandy (You're a Fine Girl)", context: "#1 in 1972 — over 1 million sold" },
      { title: "Jimmy Loves Mary-Anne", context: "#33 in 1973 — Looking Glass follow-up" },
      { title: "Golden Rainbow", context: "Looking Glass deep cut" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 3,
    revealed: true,
    wikipediaUrl: "https://en.wikipedia.org/wiki/Elliot_Lurie",
    metaTitle:
      "Elliot Lurie Live in Detroit — Saturday July 18, 2026 | Yacht Rock Detroit",
    metaDescription:
      "Elliot Lurie, the voice of Looking Glass and writer of 'Brandy (You're a Fine Girl),' performs live in Detroit on Saturday, July 18, 2026 at The Roostertail on the Detroit River.",
    metaKeywords:
      "elliot lurie detroit, looking glass brandy live, elliot lurie tour 2026, brandy youre a fine girl live, elliot lurie tickets",
  },
  {
    slug: "peter-beckett",
    name: "Peter Beckett",
    role: 'The Voice of Player — "Baby Come Back"',
    shortTagline: 'The voice behind the #1 hit "Baby Come Back."',
    bigStatement:
      'The lead singer of Player. The hit you know by heart. Saturday in Detroit.',
    image: "/artists/peter-beckett.jpg",
    imageAlt: "Peter Beckett performing live with arm raised, blue arena stage lights behind him",
    bio: "Peter Beckett is the voice behind Player's 'Baby Come Back' — the #1 hit that became a yacht rock standard. He performs live at Yacht Rock Detroit 2026 on the Detroit waterfront at The Roostertail, July 17-18.",
    longBio:
      "Peter Beckett co-wrote and sang lead on 'Baby Come Back' — the song that became Player's signature hit, spent 3 weeks at #1 in 1978, and sold over a million copies. Beckett is from Liverpool by way of Los Angeles, and he's still touring as the voice of Player. After 'Baby Come Back,' the band scored two more Top 40 hits — 'This Time I'm in It for Love' and 'Prisoner of Your Love.' All three are still in the live set. He takes the Detroit waterfront stage Saturday, July 18.",
    topSongs: [
      "Baby Come Back",
      "This Time I'm in It for Love",
      "Prisoner of Your Love",
    ],
    hits: [
      { title: "Baby Come Back", context: "#1 in 1978 — three weeks at the top" },
      { title: "This Time I'm in It for Love", context: "#10 in 1978" },
      { title: "Prisoner of Your Love", context: "#27 in 1979" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 4,
    revealed: true,
    wikipediaUrl: "https://en.wikipedia.org/wiki/Peter_Beckett",
    metaTitle:
      "Peter Beckett of Player Live in Detroit — Saturday July 18, 2026",
    metaDescription:
      "Peter Beckett, the voice of Player and lead singer on 'Baby Come Back,' performs live in Detroit on Saturday, July 18, 2026 at The Roostertail. Hear the #1 hit live on the Detroit River.",
    metaKeywords:
      "peter beckett detroit, player baby come back live, peter beckett tour 2026, baby come back live detroit, peter beckett tickets",
  },
  {
    slug: "walter-egan",
    name: "Walter Egan",
    role: '"Magnet and Steel"',
    shortTagline: 'The voice — and the songwriter — of "Magnet and Steel."',
    bigStatement:
      'Stevie Nicks. Lindsey Buckingham. And the song they helped him make.',
    image: "/artists/walter-egan.jpg",
    imageAlt: "Walter Egan with electric guitar at a vintage microphone, warm amber rock club lighting",
    bio: "Walter Egan crafted 'Magnet and Steel' with Fleetwood Mac's Stevie Nicks and Lindsey Buckingham — a yacht rock essential. He performs live on the Detroit River at Yacht Rock Detroit 2026 at The Roostertail.",
    longBio:
      "'Magnet and Steel' wasn't a fluke — it was a collaboration. Walter Egan wrote it. Lindsey Buckingham produced it and played guitar. Stevie Nicks sang the harmonies. It hit #8 on the Billboard Hot 100 in 1978, and 47 years later it's still in heavy rotation. Egan was a singer-songwriter inside the Fleetwood Mac orbit during their Rumours era — and that DNA is all over the song. He's still performing it live. Detroit gets him on Saturday, July 18.",
    topSongs: ["Magnet and Steel", "Hot Summer Nights", "Only the Lucky"],
    hits: [
      { title: "Magnet and Steel", context: "#8 in 1978 — with Buckingham and Nicks" },
      { title: "Hot Summer Nights", context: "1978 deep cut from Not Shy" },
      { title: "Only the Lucky", context: "1981 — followup album cut" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 5,
    revealed: true,
    wikipediaUrl: "https://en.wikipedia.org/wiki/Walter_Egan",
    metaTitle:
      "Walter Egan Live in Detroit — Saturday July 18, 2026 | Yacht Rock Detroit",
    metaDescription:
      "Walter Egan performs 'Magnet and Steel' live in Detroit on Saturday, July 18, 2026 at The Roostertail. The yacht rock classic — co-produced by Fleetwood Mac's Lindsey Buckingham — on the Detroit River.",
    metaKeywords:
      "walter egan detroit, magnet and steel live, walter egan tour 2026, walter egan tickets, magnet and steel detroit",
  },
  {
    slug: "party-pupils",
    name: "Party Pupils",
    role: "Yacht House Pioneers — MAX + Ryan Siegel",
    shortTagline: "Yacht rock meets dance music. They invented the bridge.",
    bigStatement:
      "They invented yacht house. Now they're docking in Detroit.",
    image: "/pp-photo.webp",
    imageMobile: "/pp-photo-sm.webp",
    logoUrl: "/pp-logo.webp",
    accentColor: "#A8FF2F",
    imageAlt: "Party Pupils — MAX and Ryan Siegel",
    bio: "Party Pupils are MAX (3× Platinum Billboard Top 20 pop star) and producer Ryan Siegel — the duo who built 'yacht house.' They remixed Christopher Cross's 'Ride Like the Wind' and dropped 'Yacht House Summer Vol 1' in March 2026. The bridge between yacht rock and dance music.",
    longBio:
      "Party Pupils aren't a DJ booking — they're a genre. MAX Schneider (yes, that MAX — the 3× Platinum vocalist behind 'Lights Down Low') and producer Ryan Siegel built something they call yacht house: mid-tempo, future-funk, weekend-ready dance music with smooth-rock DNA. They remixed Christopher Cross's 'Ride Like the Wind.' They dropped 'Yacht House Summer Vol 1' in March 2026. They cover Montell Jordan live. Saturday, July 18, they take the Detroit River — and the line between yacht rock and dance music ceases to exist.",
    topSongs: [
      "Yacht House Summer Vol 1",
      "Ride Like the Wind (Christopher Cross Remix)",
      "Lights Down Low (MAX feat. gnash)",
    ],
    hits: [
      { title: "Yacht House Summer Vol 1", context: "March 2026 — the album that named the genre" },
      { title: "Ride Like the Wind (Remix)", context: "Christopher Cross — flipped into yacht house" },
      { title: "Lights Down Low", context: "MAX's 3× Platinum Billboard Top 20 hit" },
      { title: "This Is How We Do It", context: "Montell Jordan cover — a live staple" },
      { title: "Love Me for the Weekend", context: "Mid-tempo dance original" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 7,
    revealed: true,
    externalUrl: "https://partypupils.com",
    instagramUrl: "https://www.instagram.com/partypupils",
    metaTitle:
      "Party Pupils Live in Detroit — Saturday July 18, 2026 | Yacht Rock Detroit",
    metaDescription:
      "Party Pupils — the yacht house duo behind 'Yacht House Summer Vol 1' and the Christopher Cross remix — perform live in Detroit on Saturday, July 18, 2026 at The Roostertail.",
    metaKeywords:
      "party pupils detroit, party pupils tour 2026, yacht house detroit, party pupils live, max schneider detroit, ryan siegel party pupils, yacht rock house",
  },
  {
    slug: "john-ford-coley",
    name: "John Ford Coley",
    role: '"I\'d Really Love to See You Tonight"',
    shortTagline: 'Half of England Dan & John Ford Coley. All the harmony.',
    bigStatement:
      "Two #2 hits. One unmistakable voice. Saturday on the river.",
    image: "/artists/john-ford-coley.jpg",
    imageAlt: "John Ford Coley with acoustic guitar at a vintage condenser microphone, dusky rose listening-room stage",
    bio: "John Ford Coley, of England Dan & John Ford Coley, is best known for 'I'd Really Love to See You Tonight' and 'Nights Are Forever Without You.' He takes the stage in Detroit at The Roostertail as part of the Yacht Rock Detroit 2026 lineup.",
    longBio:
      "England Dan & John Ford Coley were the soft-rock duo with the harmonies you can hear from a mile away. Their 1976 single 'I'd Really Love to See You Tonight' reached #2 on the Hot 100 and #1 on the Adult Contemporary chart. The follow-up — 'Nights Are Forever Without You' — also peaked at #10. John Ford Coley still performs as a solo act, still hits those harmonies, and he's bringing both classics to Detroit on Saturday, July 18.",
    topSongs: [
      "I'd Really Love to See You Tonight",
      "Nights Are Forever Without You",
      "Love Is the Answer",
    ],
    hits: [
      { title: "I'd Really Love to See You Tonight", context: "#2 in 1976" },
      { title: "Nights Are Forever Without You", context: "#10 in 1976" },
      { title: "We'll Never Have to Say Goodbye Again", context: "#9 in 1978" },
      { title: "Love Is the Answer", context: "Co-written with Todd Rundgren" },
    ],
    day: "saturday",
    dayLabel: "Saturday",
    dayDate: SATURDAY_DATE,
    position: 6,
    revealed: true,
    wikipediaUrl: "https://en.wikipedia.org/wiki/John_Ford_Coley",
    metaTitle:
      "John Ford Coley Live in Detroit — Saturday July 18, 2026",
    metaDescription:
      "John Ford Coley, of England Dan & John Ford Coley, performs 'I'd Really Love to See You Tonight' live in Detroit on Saturday, July 18, 2026 at The Roostertail on the Detroit River.",
    metaKeywords:
      "john ford coley detroit, england dan john ford coley live, id really love to see you tonight live, john ford coley tour 2026",
  },
];

// ── Lookups ────────────────────────────────────────────────────

export const ARTISTS_BY_SLUG = ARTISTS.reduce<Record<string, ArtistData>>(
  (acc, artist) => {
    acc[artist.slug] = artist;
    return acc;
  },
  {},
);

export const FRIDAY_ARTISTS = ARTISTS.filter((a) => a.day === "friday").sort(
  (a, b) => a.position - b.position,
);

export const SATURDAY_ARTISTS = ARTISTS.filter(
  (a) => a.day === "saturday",
).sort((a, b) => a.position - b.position);

export function getArtistBySlug(slug: string): ArtistData | undefined {
  return ARTISTS_BY_SLUG[slug];
}

export function getOtherArtists(slug: string): ArtistData[] {
  return ARTISTS.filter((a) => a.slug !== slug);
}
